import React, { useContext, useState, useEffect } from "react";
import { UserContext } from "../../CheckoutPage";
import ReactGA from "react-ga4";
import { trackData } from "../../../../Utils/attribution/CheckoutAttribution";
import { useToast } from "../../../../contexts/ToastContext";
// Add API imports for OTP, PFH verification, and address fetching
import {
  sendOtpAPI, // OTP verification
  sendOtpAPIPFH, // PFH OTP verification
  verifyOtpAPI, // OTP verification
  verifyOtpPFHAPI, // PFH verification
  getAddressFromPincodeAPI, // Address fetching from pincode
} from "../../../../Utils/CheckoutAPIs/CheckoutAPIs";
import "./V2StudentDetails.css";

/**
 * V2StudentDetails Component
 *
 * Features Added from Original Design:
 * ✅ OTP Verification System - ACTIVE (inline layout)
 * ✅ PFH (Referred Student) Verification - Available (commented out)
 * ✅ Advanced Analytics Tracking - ACTIVE
 * ✅ Mobile number verification with 4-digit OTP
 * ✅ Resend OTP functionality with 30-second timer
 * ✅ Form validation matching original FormCluster
 * ✅ Pincode field with address auto-fetch functionality
 *
 * Grid Layout Structure:
 * Row 1: Student Name | Email
 * Row 2: Mobile Number (with inline OTP) | School Name
 * Row 3: Grade Dropdown | Pincode
 * [Optional Row: School City (for non-individual registration)]
 */

const V2StudentDetails: React.FC = () => {
  const userContext = useContext(UserContext);
  const { showToast } = useToast();

  // Basic state initialization without complex logic
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [school, setSchool] = useState("");
  const [grade, setGrade] = useState<number>(0);

  // OTP Verification System - from original FormCluster
  const [pfh, setPfh] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpFieldVisible, setIsOtpFieldVisible] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const [isOtpValid, setIsOtpValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // PFH-specific states
  const [isPfhUser, setIsPfhUser] = useState(false);
  const [pfhVerificationFailed, setPfhVerificationFailed] = useState(false);
  const [showPfhSuccessPopup, setShowPfhSuccessPopup] = useState(false);

  // Pincode and address fetching states
  const [pincode, setPincode] = useState("");
  const [pincodeLoading, setPincodeLoading] = useState(false);

  // Validation states
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    school: "",
    grade: "",
    mobileNumber: "",
    otp: "", // OTP error
    sendOtpError: "", // OTP sending error
    pincode: "", // Pincode error
  });

  // Analytics tracking - matching original FormCluster implementation with OTP events
  const [trackedFields, setTrackedFields] = useState<Record<string, boolean>>(
    () => {
      const saved = sessionStorage.getItem("trackedFormFields");
      return saved
        ? JSON.parse(saved)
        : {
            mobile: false,
            name: false,
            school: false,
            grade: false,
            email: false,
            otp_sent: false, // OTP analytics - now active
            otp_verified: false, // OTP analytics - now active
            pincode: false, // Pincode analytics
          };
    }
  );

  // Validation patterns - matching original exactly
  const emailValidation = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
  const mobileValidation = /^[1-9][0-9]{9}$/;
  const dubaiMobileValidation =
    /^((\+971|971|0)?(50|51|52|54|55|56|58|4))\d{7}$/;
  const otpValidation = /^\d{4}$/; // OTP validation
  const pincodeValidation = /^\d{6}$/; // Pincode validation

  // Save tracking data to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("trackedFormFields", JSON.stringify(trackedFields));
  }, [trackedFields]);

  // Initialize form values from sessionStorage and context after component mounts
  useEffect(() => {
    try {
      // Initialize name, email, grade, pfh from sessionStorage
      const savedName = sessionStorage.getItem("name");
      const savedEmail = sessionStorage.getItem("email");
      const savedGrade = sessionStorage.getItem("grade");
      const savedPfh = sessionStorage.getItem("pfh");
      const savedPincode = sessionStorage.getItem("pincode");

      if (savedName && savedName !== "null") setName(savedName);
      if (savedEmail && savedEmail !== "null") setEmail(savedEmail);
      if (savedGrade && savedGrade !== "null" && savedGrade !== "0")
        setGrade(Number(savedGrade));
      if (savedPfh && savedPfh !== "null") {
        setPfh(savedPfh);
        // Check if this is a PFH user (pfh === "true")
        if (savedPfh === "true") {
          setIsPfhUser(true);
          console.log("🎯 PFH user detected - enabling PFH verification flow");
        }
      }
      if (
        savedPincode &&
        savedPincode !== "null" &&
        savedPincode !== "560076"
      ) {
        setPincode(savedPincode);
      } else {
        setPincode(""); // Set empty string for individual registrations or default pincode
      }

      // Initialize school based on registration type
      if (userContext.individualReg) {
        setSchool("");
      } else {
        setSchool(
          sessionStorage.getItem("school") ||
            userContext.backendSchool?.schoolName ||
            ""
        );
      }
    } catch (error) {
      console.error("Error initializing form values:", error);
      showToast("Failed to initialize form. Please refresh the page.", "error");
    }
  }, [userContext.individualReg, userContext.backendSchool?.schoolName]);

  // Handle grade URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utmGrade = urlParams.get("grade");

    if (utmGrade) {
      const gradeNum = parseInt(utmGrade, 10);
      if (!isNaN(gradeNum) && gradeNum >= 5 && gradeNum <= 12) {
        // Only set if there's no existing grade in sessionStorage
        const existingGrade = sessionStorage.getItem("grade");
        if (!existingGrade || existingGrade === "0") {
          sessionStorage.setItem("grade", utmGrade);
          setGrade(gradeNum);
          userContext?.setUserDetails?.((prev) => ({
            ...prev,
            grade: gradeNum,
          }));
          console.log(`Grade set from UTM parameter: ${utmGrade}`);
        }
      } else {
        console.warn(
          `Invalid grade parameter : ${utmGrade}. Must be between 5-12.`
        );
      }
    }
  }, [userContext?.setUserDetails]);

  // Auto-fill pincode from schoolDetails for school-based registrations
  useEffect(() => {
    if (userContext.backendSchool && !userContext.individualReg) {
      // Only auto-fill for non-individual registrations (school-based)
      const schoolPincode = userContext.backendSchool.pincode;

      console.log("🔍 V2StudentDetails Auto-fill check:", {
        schoolPincode: schoolPincode,
        currentPincode: pincode,
        individualReg: userContext.individualReg,
        shouldAutoFill:
          schoolPincode &&
          schoolPincode !== "560076" &&
          (!pincode || pincode === "560076" || pincode === ""),
      });

      // Check if we have pincode from school and current form needs auto-fill
      if (
        schoolPincode &&
        schoolPincode !== "560076" &&
        (!pincode || pincode === "560076" || pincode === "")
      ) {
        console.log(
          "✅ V2StudentDetails: Auto-filling pincode from school details:",
          schoolPincode
        );

        // Set pincode and trigger address fetch
        setPincode(schoolPincode);
        sessionStorage.setItem("pincode", schoolPincode);

        // Update userDetails context
        userContext.setUserDetails((prev: any) => ({
          ...prev,
          pincode: schoolPincode,
        }));

        // Fetch city and state from the pincode API
        fetchAddressFromPincode(schoolPincode);
      }
    }
  }, [userContext.backendSchool, userContext.individualReg, pincode]);

  // Analytics tracking function - matching original FormCluster
  const trackFieldCompletion = (fieldName: string, isValid: boolean) => {
    if (isValid && !trackedFields[fieldName]) {
      trackData(`5678_checkout_${fieldName}`);
      ReactGA.event({
        category: "Form_Completion",
        action: `5678_checkout_${fieldName}`,
        label: "Checkout_Form",
      });
      setTrackedFields((prev) => ({
        ...prev,
        [fieldName]: true,
      }));
    }
  };

  // Auto-fetch address from pincode
  const fetchAddressFromPincode = async (pincodeValue: string) => {
    if (pincodeValue.length === 6) {
      setPincodeLoading(true);
      try {
        const response = await getAddressFromPincodeAPI(pincodeValue);
        if (
          response &&
          response.data &&
          response.data.city &&
          response.data.state
        ) {
          // Update userDetails with fetched city and state
          userContext.setUserDetails((prevUserDetails: any) => ({
            ...prevUserDetails,
            city: response.data.city,
            state: response.data.state,
            pincode: pincodeValue,
          }));

          // Track pincode completion
          trackFieldCompletion("pincode", true);
          showToast("Address details fetched successfully!", "success");
        }
      } catch (error) {
        console.error("Error fetching address:", error);
        showToast(
          "Failed to fetch address from pincode. Please enter manually.",
          "warning"
        );
        setErrors((prev) => ({
          ...prev,
          pincode: "Unable to fetch address. Please enter manually.",
        }));
      } finally {
        setPincodeLoading(false);
      }
    }
  };

  // OTP Verification System - from original FormCluster
  const sendOtp = async () => {
    // Validate mobile number before sending OTP
    const isValidMobile = userContext.backendSchool?.isInternational
      ? dubaiMobileValidation.test(userContext.userDetails?.mobileNumber || "")
      : mobileValidation.test(userContext.userDetails?.mobileNumber || "");

    if (!isValidMobile) {
      setErrors((prevError) => ({
        ...prevError,
        sendOtpError: "Please enter a valid mobile number first",
      }));
      return;
    }

    setIsLoading(true);
    try {
      // Use PFH-specific OTP API if this is a PFH user
      const response = isPfhUser
        ? await sendOtpAPIPFH(userContext.userDetails?.mobileNumber || "")
        : await sendOtpAPI(userContext.userDetails?.mobileNumber || "");

      if (response.status === "Success") {
        setIsOtpFieldVisible(true);
        setIsResendEnabled(false);
        setResendTimer(30);
        startResendTimer();
        setErrors((prevError) => ({
          ...prevError,
          sendOtpError: "",
        }));

        const successMessage =
          "OTP sent successfully! Check your mobile for the 4-digit code.";
        showToast(successMessage, "success");

        if (trackedFields.mobile) {
          trackData(
            isPfhUser ? "5678_pfh_send_otp_button" : "5678_checkout_otp_sent"
          );
          ReactGA.event({
            category: isPfhUser ? "PFH_Verification" : "Form_Completion",
            action: isPfhUser
              ? "5678_pfh_send_otp_button"
              : "5678_checkout_otp_sent",
            label: isPfhUser ? "Discount_Flow" : "Checkout_Form",
          });
          setTrackedFields((prev) => ({
            ...prev,
            otp_sent: true,
          }));
        }
      } else {
        setErrors((prevError) => ({
          ...prevError,
          sendOtpError: "Error sending OTP, Please try again",
        }));
      }
    } catch (err: any) {
      setErrors((prevError) => ({
        ...prevError,
        sendOtpError: "Error sending OTP, Please try again",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // OTP Verification function
  const verifyOtp = async () => {
    setIsLoading(true);
    setErrors((prev) => ({ ...prev, otp: "" }));
    const nfo2024Param = sessionStorage.getItem("nfo2024") === "true";

    try {
      const response =
        isPfhUser || nfo2024Param
          ? await verifyOtpPFHAPI(
              userContext.userDetails?.mobileNumber || "",
              otp
            )
          : await verifyOtpAPI(
              userContext.userDetails?.mobileNumber || "",
              otp
            );

      if (response.status !== "Success") {
        setErrors((prev) => ({
          ...prev,
          otp: "Invalid OTP. Please try again.",
        }));
        return;
      } else {
        userContext.setIsOtpVerified(true);
        setIsOtpFieldVisible(false);

        if (isPfhUser || nfo2024Param) {
          // For PFH or NFO2024 users, check if they're verified for discount
          const pfhVerified =
            (typeof response.data === "object" && response.data?.pfh) || false;
          const nfo2024Verified =
            (typeof response.data === "object" && response.data?.nfo2024) ||
            false;

          userContext.setPfhVerified(pfhVerified);
          userContext.setNfo2024Verified(nfo2024Verified);

          // Store verification results in sessionStorage
          sessionStorage.setItem("pfhVerified", pfhVerified.toString());
          sessionStorage.setItem("nfo2024Verified", nfo2024Verified.toString());

          if (pfhVerified) {
            setShowPfhSuccessPopup(true);
            showToast(
              "PFH verification successful! You're eligible for the discount.",
              "success"
            );
          } else if (nfo2024Verified) {
            showToast(
              "NFO2024 verification successful! You're eligible for the discount.",
              "success"
            );
          } else {
            setPfhVerificationFailed(true);
            showToast(
              "Verification successful! Standard pricing applies.",
              "warning"
            );
          }

          trackData("5678_pfh_verify_otp_button");
          ReactGA.event({
            category: "PFH_Verification",
            action: "5678_pfh_verify_otp_button",
            label: "Discount_Flow",
          });
        } else {
          showToast("Mobile number verified successfully!", "success");
          trackData("5678_checkout_otp_verified");
          ReactGA.event({
            category: "Form_Completion",
            action: "5678_checkout_otp_verified",
            label: "Checkout_Form",
          });
        }

        if (!trackedFields.otp_verified) {
          setTrackedFields((prev) => ({
            ...prev,
            otp_verified: true,
          }));
        }
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      showToast("Failed to verify OTP. Please try again.", "error");
      setErrors((prev) => ({ ...prev, otp: "Invalid OTP. Please try again." }));
    } finally {
      setIsLoading(false);
    }
  };

  // OTP handling logic
  const handleOtp = () => {
    if (!isOtpFieldVisible) {
      sendOtp();
    } else if (isResendEnabled) {
      handleResendOtp();
    }
  };

  // Timer for resend OTP
  const startResendTimer = () => {
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsResendEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Resend OTP handler
  const handleResendOtp = () => {
    if (isResendEnabled) {
      sendOtp();
    }
  };

  // Update userDetails when local state changes
  useEffect(() => {
    try {
      userContext.setUserDetails((prevUserDetails: any) => ({
        ...prevUserDetails,
        name,
        email,
        school: userContext.individualReg
          ? school
          : userContext.backendSchool?.schoolName || "",
        grade,
        pincode,
      }));
      console.log("issue");
      // Persist to sessionStorage like original
      sessionStorage.setItem("name", name);
      sessionStorage.setItem("email", email);
      sessionStorage.setItem("school", school);
      sessionStorage.setItem("grade", grade.toString());
      sessionStorage.setItem("pincode", pincode);
    } catch (error) {
      console.error("Error updating userDetails:", error);
      showToast("Failed to update form data. Please try again.", "error");
    }
  }, [
    name,
    email,
    school,
    grade,
    pincode,
    userContext.individualReg,
    userContext.backendSchool?.schoolName,
    userContext.setUserDetails,
  ]);

  const handleGradeSelect = (selectedGrade: number) => {
    // Validation - matching original exactly
    if (selectedGrade < 5 || selectedGrade > 12) {
      setErrors({
        ...errors,
        grade: "Please enter a valid grade between 5 - 12.",
      });
    } else {
      setErrors({ ...errors, grade: "" });
      setGrade(selectedGrade);
      sessionStorage.setItem("grade", selectedGrade.toString());
      userContext.setUserDetails((prevUserDetails: any) => ({
        ...prevUserDetails,
        grade: selectedGrade,
      }));
      // Track analytics
      trackFieldCompletion("grade", true);
    }
  };

  // Name change handler - matching original validation
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    sessionStorage.setItem("name", value);

    // Validation
    const nameError = value.length < 3 ? "Please enter a valid name." : "";
    setErrors({ ...errors, name: nameError });

    // Update context
    userContext.setUserDetails((prevUserDetails: any) => ({
      ...prevUserDetails,
      name: value,
    }));

    // Track analytics
    if (value.length >= 3) {
      trackFieldCompletion("name", true);
    }
  };

  // Email change handler - matching original validation
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    sessionStorage.setItem("email", value);

    // Validation
    const emailError = emailValidation.test(value)
      ? ""
      : "Please enter a valid email.";
    setErrors({ ...errors, email: emailError });

    // Update context
    userContext.setUserDetails((prevUserDetails: any) => ({
      ...prevUserDetails,
      email: value,
    }));

    // Track analytics
    if (emailValidation.test(value)) {
      trackFieldCompletion("email", true);
    }
  };

  // School change handler - matching original validation
  const handleSchoolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSchool(value);
    sessionStorage.setItem("school", value);

    // Validation - only for individual registration
    const schoolError =
      userContext.individualReg && value.length < 3
        ? "Please enter a valid school name."
        : "";
    setErrors({ ...errors, school: schoolError });

    // Update context
    userContext.setUserDetails((prevUserDetails: any) => ({
      ...prevUserDetails,
      school: value,
    }));

    // Track analytics
    if (value.length >= 3) {
      trackFieldCompletion("school", true);
    }
  };

  // Mobile change handler - matching original validation
  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Reset OTP state when mobile number changes
    if (value !== userContext.userDetails?.mobileNumber) {
      setOtp("");
      setIsOtpFieldVisible(false);
      setIsOtpValid(false);
      setIsResendEnabled(false);
      setResendTimer(30);
      userContext.setIsOtpVerified(false);
      setErrors((prev) => ({ ...prev, otp: "", sendOtpError: "" }));
    }

    // Validation
    const isValidMobile = userContext.backendSchool?.isInternational
      ? dubaiMobileValidation.test(value)
      : mobileValidation.test(value);
    const mobileError = isValidMobile
      ? ""
      : "Please enter a valid mobile number.";
    setErrors({ ...errors, mobileNumber: mobileError });

    // Update context
    userContext.setUserDetails((prevUserDetails: any) => ({
      ...prevUserDetails,
      mobileNumber: value,
    }));
    sessionStorage.setItem("mobileNumber", value);

    // Track analytics
    if (isValidMobile) {
      trackFieldCompletion("mobile", true);
    }
  };

  // Pincode change handler
  const handlePincodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Only allow numeric input for pincode
    if (/\D/.test(value)) {
      return; // Exit if non-numeric input is detected
    }

    setPincode(value);
    sessionStorage.setItem("pincode", value);

    // Clear previous errors
    setErrors((prev) => ({ ...prev, pincode: "" }));

    // Validation
    if (value.length === 6) {
      fetchAddressFromPincode(value);
    } else if (value.length > 0) {
      setErrors((prev) => ({
        ...prev,
        pincode: "Pincode must be 6 digits",
      }));
    }

    // Update context
    userContext.setUserDetails((prevUserDetails: any) => ({
      ...prevUserDetails,
      pincode: value,
    }));
  };

  // Add null check for userContext before rendering
  if (!userContext) {
    console.error("V2StudentDetails: userContext is null or undefined");
    return <div>Loading...</div>;
  }

  return (
    <div className="v2-section">
      <div className="v2-section-header">
        <div className="v2-section-number">1</div>
        <h2 className="v2-section-title">Student Details</h2>
      </div>

      <div className="v2-form-grid">
        {/* RESPONSIVE LAYOUT: 
            - Desktop (>1024px): 2 columns side by side
            - Tablet (769-1024px): 2 columns side by side  
            - Mobile (<768px): 1 column stacked vertically
        */}

        {/* Row 1: Student Name | Email */}
        <div className="v2-form-row">
          <div className="v2-form-group">
            <label className="v2-label">Student Name *</label>
            <input
              type="text"
              className={`v2-input ${errors.name ? "error" : ""}`}
              value={name}
              onChange={handleNameChange}
              onBlur={(e) => {
                const value = e.target.value;
                if (value.length <= 0) {
                  setErrors({ ...errors, name: "Please enter a valid name." });
                } else {
                  setErrors({ ...errors, name: "" });
                }
              }}
              placeholder="Enter student name"
            />
            {errors.name && (
              <div className="v2-error-message">{errors.name}</div>
            )}
          </div>
          <div className="v2-form-group">
            <label className="v2-label">Email *</label>
            <input
              type="email"
              className={`v2-input ${errors.email ? "error" : ""}`}
              value={email}
              onChange={handleEmailChange}
              onBlur={(e) => {
                const value = e.target.value;
                if (value.length <= 0) {
                  setErrors({
                    ...errors,
                    email: "Please enter a valid email.",
                  });
                } else {
                  setErrors({ ...errors, email: "" });
                }
              }}
              placeholder="Enter email address"
            />
            {errors.email && (
              <div className="v2-error-message">{errors.email}</div>
            )}
          </div>
        </div>

        {/* Row 2: Mobile Number (with OTP) | School Name */}
        <div className="v2-form-row">
          <div className="v2-form-group">
            <label className="v2-label">
              {isPfhUser ? "Mobile Number *" : "Mobile Number *"}
            </label>
            {isPfhUser && (
              <div style={{ fontSize: "10px", color: "#666" }}>
                Enter your mobile number to instantly see the special discounts,
                rewards, and offers waiting just for you.
              </div>
            )}
            <input
              type="tel"
              className={`v2-input ${errors.mobileNumber ? "error" : ""}`}
              value={userContext.userDetails?.mobileNumber || ""}
              disabled={userContext.isOtpVerified}
              onChange={handleMobileChange}
              onBlur={(e) => {
                const value = e.target.value;
                const isValidMobile = userContext.backendSchool?.isInternational
                  ? dubaiMobileValidation.test(value)
                  : mobileValidation.test(value);
                if (!isValidMobile) {
                  setErrors({
                    ...errors,
                    mobileNumber: "Please enter a valid mobile number.",
                  });
                } else {
                  setErrors({ ...errors, mobileNumber: "" });
                }
              }}
              placeholder={
                isPfhUser ? "Enter mobile number" : "Enter mobile number"
              }
            />
            {errors.mobileNumber && (
              <div className="v2-error-message">{errors.mobileNumber}</div>
            )}

            {/* OTP Verification - Compact inline design */}
            {errors.sendOtpError && (
              <div className="v2-error-message" style={{ marginTop: "4px" }}>
                {errors.sendOtpError}
              </div>
            )}

            {!errors.mobileNumber &&
              userContext.userDetails?.mobileNumber &&
              !userContext.isOtpVerified && (
                <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
                  {isOtpFieldVisible && (
                    <input
                      type="text"
                      className={`v2-input ${errors.otp ? "error" : ""}`}
                      value={otp}
                      disabled={isLoading}
                      onChange={(e) => {
                        const value = e.target.value;
                        setOtp(value);
                        setIsOtpValid(otpValidation.test(value));
                      }}
                      placeholder="4-digit OTP"
                      maxLength={4}
                      style={{ flex: "1" }}
                    />
                  )}
                  <button
                    className="v2-otp-button"
                    onClick={isOtpFieldVisible ? verifyOtp : handleOtp}
                    disabled={
                      isLoading ||
                      (isOtpFieldVisible && !isOtpValid) ||
                      (!isOtpFieldVisible &&
                        (!!errors.mobileNumber ||
                          !userContext.userDetails?.mobileNumber))
                    }
                    style={{
                      minWidth: isOtpFieldVisible ? "80px" : "100px",
                      flex: isOtpFieldVisible ? "0 0 auto" : "1",
                    }}
                  >
                    {isLoading ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        <div className="v2-loading-spinner"></div>
                        {isOtpFieldVisible ? "Verifying..." : "Sending..."}
                      </div>
                    ) : (
                      <>
                        {isOtpFieldVisible
                          ? "Verify"
                          : isPfhUser
                          ? "Check My Offers"
                          : "Send OTP"}
                      </>
                    )}
                  </button>
                </div>
              )}

            {isOtpFieldVisible && !isResendEnabled && (
              <div
                style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}
              >
                Resend in {resendTimer}s
              </div>
            )}

            {isOtpFieldVisible && isResendEnabled && (
              <button
                onClick={handleOtp}
                disabled={isLoading}
                style={{
                  marginTop: "4px",
                  background: "none",
                  border: "none",
                  color: "#376CAA",
                  cursor: "pointer",
                  fontSize: "12px",
                  textDecoration: "underline",
                }}
              >
                Resend OTP
              </button>
            )}

            {errors.otp && (
              <div className="v2-error-message" style={{ marginTop: "4px" }}>
                {errors.otp}
              </div>
            )}

            {userContext.isOtpVerified && (
              <div
                className="v2-verification-success"
                style={{ fontSize: "12px" }}
              >
                {isPfhUser ? (
                  pfhVerificationFailed ? (
                    <span style={{ color: "#f59e0b" }}>
                      No offers available — standard pricing applies.
                    </span>
                  ) : (
                    <span style={{ color: "#10b981" }}>
                      ✓ Verified - Discount Applied
                    </span>
                  )
                ) : (
                  "✓ Mobile number verified"
                )}
              </div>
            )}
          </div>

          <div className="v2-form-group">
            <label className="v2-label">
              {userContext.individualReg ? "School Name *" : "School"}
            </label>
            <input
              type="text"
              className={`v2-input ${errors.school ? "error" : ""}`}
              value={school}
              disabled={!userContext.individualReg}
              onChange={handleSchoolChange}
              onBlur={(e) => {
                const value = e.target.value;
                if (userContext.individualReg && value.length <= 0) {
                  setErrors({
                    ...errors,
                    school: "Please enter a valid school name.",
                  });
                } else {
                  setErrors({ ...errors, school: "" });
                }
              }}
              placeholder="Enter school name"
            />
            {errors.school && (
              <div className="v2-error-message">{errors.school}</div>
            )}
          </div>
        </div>

        {/* School City Field - Only visible for non-individual registrations */}
        {!userContext.individualReg && (
          <div className="v2-form-row">
            <div className="v2-form-group">
              <label className="v2-label">School City</label>
              <input
                type="text"
                className="v2-input v2-input-disabled"
                value={userContext.backendSchool?.city || ""}
                disabled={true}
                readOnly
                placeholder="School city"
              />
            </div>
            <div className="v2-form-group">
              {/* Empty div for proper grid layout */}
            </div>
          </div>
        )}

        {/* Row 3: Grade Select | Pincode */}
        <div className="v2-form-row">
          <div className="v2-form-group">
            <label className="v2-label">Grade *</label>
            <div className="v2-grade-select-container">
              <select
                className={`v2-grade-select ${errors.grade ? "error" : ""}`}
                value={grade}
                onChange={(e) => {
                  const selectedGrade = parseInt(e.target.value);
                  handleGradeSelect(selectedGrade);
                }}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #e5e7eb",
                  background: "#f9fafb",
                  borderRadius: "6px",
                  fontSize: "14px",
                  color: "#1f2937",
                  minHeight: "44px",
                  fontFamily: "'OggTextRegular'",
                  cursor: "pointer",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  appearance: "none",
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23374151' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  backgroundSize: "16px",
                  paddingRight: "40px",
                }}
              >
                <option value={0}>Select Grade</option>
                <option value={5}>Grade 5</option>
                <option value={6}>Grade 6</option>
                <option value={7}>Grade 7</option>
                <option value={8}>Grade 8</option>
                <option value={9}>Grade 9</option>
                <option value={10}>Grade 10</option>
                <option value={11}>Grade 11</option>
                <option value={12}>Grade 12</option>
              </select>
            </div>
            {errors.grade && (
              <div className="v2-error-message">{errors.grade}</div>
            )}
            <div
              style={{
                fontSize: "12px",
                color: "#235188",
                marginTop: "4px",
                textAlign: "center",
              }}
            >
              Enter the applicable grade for academic year 2026-27
            </div>
          </div>
          <div className="v2-form-group">
            <label className="v2-label">Pincode *</label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                className={`v2-input ${errors.pincode ? "error" : ""} ${
                  !userContext.individualReg ? "v2-input-disabled" : ""
                }`}
                value={pincode}
                disabled={pincodeLoading || !userContext.individualReg}
                onChange={handlePincodeChange}
                placeholder="Enter pincode"
                maxLength={6}
              />
              {pincodeLoading && (
                <div
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <div className="v2-loading-spinner"></div>
                  <span style={{ fontSize: "12px", color: "#666" }}>
                    Fetching...
                  </span>
                </div>
              )}
            </div>
            {pincodeLoading && (
              <div className="v2-loading-text" style={{ marginTop: "5px" }}>
                <span>Looking up city and state</span>
                <div className="v2-loading-dot">.</div>
                <div className="v2-loading-dot">.</div>
                <div className="v2-loading-dot">.</div>
              </div>
            )}
            {errors.pincode && (
              <div className="v2-error-message">{errors.pincode}</div>
            )}
          </div>
        </div>
      </div>

      {/* PFH Success Popup */}
      {showPfhSuccessPopup && (
        <div className="pfh-success-popup-overlay">
          <div className="pfh-success-popup">
            <div className="pfh-success-popup-header">
              <h3>🎉 Congratulations!</h3>
            </div>
            <div className="pfh-success-popup-body">
              <p>
                Your verification was successful! You're eligible for exclusive
                discounts and special pricing.
              </p>
              <div className="pfh-success-benefits">
                <div className="benefit-item">
                  <span className="benefit-icon">💰</span>
                  <span>Special discount pricing applied</span>
                </div>
              </div>
            </div>
            <div className="pfh-success-popup-footer">
              <button
                className="pfh-popup-continue-btn"
                onClick={() => {
                  setShowPfhSuccessPopup(false);
                  trackData("5678_pfh_success_popup_continue");
                  ReactGA.event({
                    category: "PFH_Verification",
                    action: "5678_pfh_success_popup_continue",
                    label: "Discount_Flow",
                  });
                }}
              >
                Continue to Checkout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default V2StudentDetails;
